import { getOperationsMaintenanceCostsTotal } from "../../../calculations/operationMaintenanceCosts/operationsMaintenanceCosts";
import { getSafetyCostsTotal } from "../../../calculations/safetycosts/safetyCosts";
import { getPlanningConstructionCostsTotal } from "../../../calculations/planningConstructionCosts/planningConstructionCosts";
import { BENEFIT_COST_KEYS, COST_KEYS } from "../../../constants/costConstants";
import {
  COST_PARAMETERS_STORAGE_KEYS,
  STEP_STORAGE_KEYS,
} from "../../../constants/storageConstants";
import { getDiscountRate } from "../costParameters/costParametersDefaultsHelpers";
import { getCrashTypeCosts, getCrashesPerYear } from "./outputsSafetyHelpers";
import { errDiv } from "../../../util/mathHelpers";

/**
 *
 * @param {object} baseIntxDetails intersection object for the chosen benefit cost base case intersection
 * @param {object[]} intersectionsWithCosts intersections array from projectData with cost totals added to the outputs.total section
 * @returns array of table row values
 */
export function getBenefitCostRows({
  baseIntxDetails,
  intersectionsWithCosts,
}) {
  const baseIntxTotal = baseIntxDetails.output.total;
  return intersectionsWithCosts
    .filter(({ isCurrentIntersection }) => !isCurrentIntersection) // skip the base case
    .map((intersection) => {
      const currentTotal = intersection.output.total;
      const autoDelayDelta =
        baseIntxTotal[COST_KEYS.AUTO_DELAY] -
        currentTotal[COST_KEYS.AUTO_DELAY];
      const truckDelayDelta =
        baseIntxTotal[COST_KEYS.TRUCK_DELAY] -
        currentTotal[COST_KEYS.TRUCK_DELAY];
      const safetyDelta =
        baseIntxTotal[COST_KEYS.SAFETY] - currentTotal[COST_KEYS.SAFETY];
      const benefitDelta = autoDelayDelta + truckDelayDelta + safetyDelta;
      const costDelta =
        currentTotal[COST_KEYS.POST_OPENING] +
        currentTotal[COST_KEYS.PLANNING_CONSTRUCTION_ROW] -
        baseIntxTotal[COST_KEYS.POST_OPENING] -
        baseIntxTotal[COST_KEYS.PLANNING_CONSTRUCTION_ROW];
      return {
        id: intersection.id,
        [BENEFIT_COST_KEYS.INTERSECTION]: intersection.uniqueName,
        [BENEFIT_COST_KEYS.AUTO_DELAY]: autoDelayDelta,
        [BENEFIT_COST_KEYS.TRUCK_DELAY]: truckDelayDelta,
        [BENEFIT_COST_KEYS.SAFETY]: safetyDelta,
        [BENEFIT_COST_KEYS.VALUE_OF_BENEFITS]: benefitDelta,
        [BENEFIT_COST_KEYS.VALUE_OF_COSTS]: costDelta,
        [BENEFIT_COST_KEYS.BC_RATIO]: errDiv(benefitDelta, costDelta),
        [BENEFIT_COST_KEYS.DELAY_BC_RATIO]: errDiv(
          autoDelayDelta + truckDelayDelta,
          costDelta
        ),
        [BENEFIT_COST_KEYS.SAFETY_BC_RATIO]: errDiv(safetyDelta, costDelta),
      };
    });
}

/**
 *
 * @param {object} projectData
 * @returns object[] where each element of the array is an object with info for each intersection being analyzed
 */
export const getIntersectionsWithCosts = (projectData) => {
  const { analysisType, openingYear, designYear } =
    projectData[STEP_STORAGE_KEYS.GLOBAL_INPUT_DATA];
  const discountRate = getDiscountRate(projectData);
  const intersections = projectData[STEP_STORAGE_KEYS.INTERSECTIONS];

  return intersections.map((intersection) => {
    const operationsMaintenanceCostsPeriod =
      intersection[COST_PARAMETERS_STORAGE_KEYS.COST_PARAMETERS][
        COST_PARAMETERS_STORAGE_KEYS.OPERATIONS_AND_MAINTENANCE
      ] ??
      window.PENN_DOT_CONSTANTS.OPERATIONS_MAINTENANCE_COSTS[analysisType][
        intersection.type
      ];

    const { designCost, constructionCost, rightOfWayCost } =
      intersection[COST_PARAMETERS_STORAGE_KEYS.COST_PARAMETERS];

    return {
      ...intersection,
      output: {
        total: {
          [COST_KEYS.AUTO_DELAY]: intersection.costs[COST_KEYS.AUTO_DELAY],
          [COST_KEYS.PLANNING_CONSTRUCTION_ROW]:
            getPlanningConstructionCostsTotal({
              designCost,
              constructionCost,
              rightOfWayCost,
            }),
          [COST_KEYS.POST_OPENING]: getOperationsMaintenanceCostsTotal({
            openingYear,
            designYear,
            discountRate,
            operationsMaintenanceCostsPeriod,
          }),
          [COST_KEYS.SAFETY]: getSafetyCostsTotal({
            crashesPerYear: getCrashesPerYear({ projectData, intersection }),
            crashTypeCosts: getCrashTypeCosts(projectData),
            designYear,
            discountRate: getDiscountRate(projectData),
            openingYear,
          }),
          [COST_KEYS.TRUCK_DELAY]: intersection.costs[COST_KEYS.TRUCK_DELAY],
        },
      },
    };
  });
};

/**
 * there are only cells for 6 intersection alternatives in the ice form spreadsheet
 */
export const ICE_FORM_XLSX_INTX_CAPACITY = 6;
export const ICE_FORM_XLSX_INTX_CAPACITY_MESSAGE =
  "Ice form can not handle more than 6 intersection alternatives";
